import React from "react";
import { SCORING_TYPE } from "@lemonadetraining/contracts";
import {
  computeFillPercent,
  levelClearedFor,
  nextVisibleLevel,
  pickMilestones,
  type AxisDetailIndicatorWithMeasure,
} from "./axisDetailHelpers";
import { formatMeasureValue } from "../../lib/measureFormat";

export interface IndicatorProgressBarProps {
  indicator: AxisDetailIndicatorWithMeasure;
  /** Lower bound of the bar's value range (defaults to 0). */
  floor?: number;
  /**
   * Upper bound of the bar's value range. Defaults to the indicator's target
   * so the bar visualizes "how close to next level" without an explicit
   * caller-provided ceiling.
   */
  ceiling?: number;
}

const BAR_HEIGHT_PX = 12;

const IndicatorProgressBar: React.FC<IndicatorProgressBarProps> = ({
  indicator,
  floor: floorProp,
  ceiling,
}) => {
  // "Descending value range" (worst = larger value, best = smaller) is decided
  // by the scoring SHAPE (`lowerIsBetter`, e.g. Fran), NOT the unit. A
  // `time` + `higherIsBetter` isometric hold ascends like kg/reps.
  const isReverseScored = indicator.scoringType === SCORING_TYPE.LOWER_IS_BETTER;
  // Reverse-scored bars span Lv 0 worst (left, larger value) → Lv 100 best
  // (right, smaller value). The Lv 0 anchor is published by
  // `computeAxisDetail` on these indicators as `levelMilestones[0]`; use it
  // as the bar floor when the caller doesn't override.
  const lv0Anchor = indicator.levelMilestones[0];
  const defaultFloor =
    isReverseScored && typeof lv0Anchor === "number" && Number.isFinite(lv0Anchor) ? lv0Anchor : 0;
  const floor = floorProp ?? defaultFloor;
  const resolvedCeiling = ceiling ?? indicator.targetValue;
  // Unified fill formula. `computeFillPercent` handles both ascending
  // (higherIsBetter: floor=0 < ceiling) and descending (lowerIsBetter:
  // floor=600 > ceiling=100) ranges in a single code path — the linear
  // formula is symmetric in sign so a 180s time at floor=600/ceiling=100
  // yields the same 84% the math intuitively predicts.
  const fillPct = computeFillPercent(indicator.currentValue, floor, resolvedCeiling);
  const milestones = pickMilestones(indicator.levelMilestones, floor, resolvedCeiling);

  // Single source of truth for value text — the shared formatter handles
  // every unit (kg/reps/cal/distance suffixes, time as mm:ss, binary as
  // Unlocked/Not yet). No bespoke `${v}${suffix}` concatenation here.
  const showValue = (v: number): string => formatMeasureValue(v, indicator.measureType);

  // ARIA 1.2 requires aria-valuenow ∈ [aria-valuemin, aria-valuemax]. For
  // forward-scored bars we expose the value range directly (clamped at the
  // ceiling). For reverse-scored bars the value-axis decreases as score rises
  // — exposing raw seconds clamped to `target` would have screen readers
  // announce "100" for a 300s Fran (target=100s), implying "at the target"
  // when the athlete is actually 3× slower. Switch the SR scale to
  // fill-percent (0–100), which matches the visual fill the bar draws.
  const ariaValueNow = isReverseScored
    ? Math.round(fillPct * 100)
    : Math.max(floor, Math.min(indicator.currentValue, resolvedCeiling));
  const ariaValueMin = isReverseScored ? 0 : floor;
  const ariaValueMax = isReverseScored ? 100 : resolvedCeiling;

  // AC6 enumerates five fields: movement name, current value, target value,
  // current score, level. `currentLevel` is the per-movement score 0–100; the
  // level descriptor is the highest visible-milestone tier the athlete has
  // cleared (0 / 50 / 60 / 70 / 80) so navigating-by-progressbar SR users
  // hear the same level signal the markers expose visually.
  const cleared = levelClearedFor(indicator.currentLevel);
  const levelLabel = cleared === 0 ? "below Lv 50" : `Lv ${cleared}`;
  const scoreSegment = `Score ${indicator.currentLevel} of 100. Level ${levelLabel}.`;

  const ariaLabel = isReverseScored
    ? `${indicator.movementName}: ${showValue(indicator.currentValue)} ` +
      `(target ${showValue(indicator.targetValue)}, lower is better). ` +
      scoreSegment
    : `${indicator.movementName}: ${showValue(indicator.currentValue)} of ` +
      `${showValue(indicator.targetValue)} target. Range ${showValue(floor)} to ` +
      `${showValue(resolvedCeiling)}. ` +
      scoreSegment;

  const testIDBase = `indicator-${indicator.movementID}`;

  return (
    <div className="flex flex-col gap-1.5" data-testid={`${testIDBase}-row`}>
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-sm font-medium">{indicator.movementName}</span>
        <span className="text-xs tabular-nums text-muted-foreground">
          {showValue(indicator.currentValue)} / {showValue(indicator.targetValue)}
        </span>
      </div>

      {/* Custom progressbar — the level milestones need to overlay the bar at
          fixed value-positions, which the shadcn `Progress` primitive's slot
          structure doesn't expose cleanly. We wear the role/aria-* hat
          ourselves so screen readers still announce the value range. */}
      <div className="relative">
        {/* LT-344: the Lemonade-yellow `--primary` fill is only ~1.32:1 vs the
            `--muted` track — below the WCAG 1.4.11 3:1 non-text minimum, so the
            fill's extent isn't perceivable for low-vision users. Rather than
            re-tint the global brand color, draw a `--muted-foreground` boundary
            around the bar: that token is 4.97:1 vs the track, 5.71:1 vs the
            page, and 3.76:1 vs the yellow fill itself — all ≥3:1 — so the
            filled portion is delineated regardless of surface. The fill is
            inset inside this bordered, overflow-hidden track. Verified in
            lib/wcagContrast.test.ts. */}
        <div
          data-testid={`${testIDBase}-bar`}
          role="progressbar"
          aria-label={ariaLabel}
          aria-valuenow={ariaValueNow}
          aria-valuemin={ariaValueMin}
          aria-valuemax={ariaValueMax}
          className="w-full overflow-hidden rounded-full border border-muted-foreground bg-muted"
          style={{ height: BAR_HEIGHT_PX }}
        >
          <div
            data-testid={`${testIDBase}-fill`}
            className="h-full bg-primary transition-[width] duration-500 ease-out motion-reduce:transition-none"
            style={{ width: `${fillPct * 100}%` }}
          />
        </div>

        {milestones.length > 0 ? (
          <div
            aria-hidden="false"
            className="pointer-events-none absolute inset-x-0 top-1/2 -translate-y-1/2"
            style={{ height: BAR_HEIGHT_PX + 8 }}
          >
            {milestones.map((m) => {
              const left = computeFillPercent(m.value, floor, resolvedCeiling) * 100;
              return (
                <div
                  key={`Lv${m.level}`}
                  data-testid={`${testIDBase}-marker-Lv${m.level}`}
                  role="img"
                  aria-label={`Level ${m.level} milestone at ${showValue(m.value)}.`}
                  className="absolute top-0 -translate-x-1/2"
                  style={{ left: `${left}%`, height: "100%" }}
                >
                  <div className="h-full w-0.5 bg-foreground/60" />
                </div>
              );
            })}
          </div>
        ) : null}
      </div>

      {milestones.length > 0 ? (
        <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] tabular-nums text-muted-foreground">
          {milestones.map((m) => (
            <span key={`caption-Lv${m.level}`}>
              Lv {m.level}: {showValue(m.value)}
            </span>
          ))}
        </div>
      ) : null}

      {/* "X points from Lv N" line. Hidden for binary movements (single-step
          unlock has no in-between progress) and when the athlete has cleared
          the highest visible milestone. */}
      {indicator.scoringType !== SCORING_TYPE.BINARY
        ? renderProgressLine(indicator.currentLevel, testIDBase)
        : null}
    </div>
  );
};

function renderProgressLine(currentLevel: number, testIDBase: string): React.ReactNode {
  const next = nextVisibleLevel(currentLevel);
  if (next === null) {
    return (
      <p className="text-[11px] text-muted-foreground" data-testid={`${testIDBase}-progress-line`}>
        Lv 80+ unlocked — keep pushing.
      </p>
    );
  }
  const gap = Math.max(1, Math.round(next - currentLevel));
  const noun = gap === 1 ? "point" : "points";
  return (
    <p className="text-[11px] text-muted-foreground" data-testid={`${testIDBase}-progress-line`}>
      {`${gap} ${noun} from Lv ${next}.`}
    </p>
  );
}

export default IndicatorProgressBar;
